import React, { ReactElement } from 'react';
import { FdrDisrupters } from 'components/domain/fdr-disrupter';
import { FdrHeroSection } from 'components/domain/fdr-hero-section';
import FdrPageComponentsWithCustomBenefits from 'components/domain/fdr-page-components/fdr-page-components-with-custom-benefits';
import {
  FdrAdventureTravelContent,
  FdrDisruptersContainer,
  FdrHotelContent,
  FdrPdpDescription,
  FdrRoundTripContent,
  useFdrPdpContent,
} from 'components/domain/fdr-pdp';
import { FdrPdpRecommendations } from 'components/domain/fdr-pdp/fdr-pdp-recommendations';
import {
  isFdrAdventureTravelProduct,
  isFdrHotelProduct,
  isFdrRoundTripProduct,
} from 'components/domain/fdr-product/fdr-product.utils';
import { fdrPdpPageVars } from 'components/page/pdp/pdp.vars';
import { mapImageCriteriaField } from 'components/util/criterias/mapImageCriteriaField';
import { useFdrProductPageQuery } from 'schemas/query/product/fdr-product-page.generated';

export { getServerSideProps } from 'components/page/pdp/pdp.gssp';

interface IPdpPageProps {
  id: string;
}

export default function PdpPage({ id }: IPdpPageProps): ReactElement {
  const queryResult = useFdrProductPageQuery({
    variables: {
      id,
      ...fdrPdpPageVars,
    },
  });

  const product = queryResult?.data?.fdrProduct;
  const productPageContent = useFdrPdpContent(product);

  const shareDisabled =
    (product?.__typename === 'FdrHotel' && !product?.flags?.extraOrdinary) ||
    product?.__typename === 'FdrRoundTrip';

  if (!productPageContent) return null;

  function renderProductContent(): ReactElement {
    if (isFdrAdventureTravelProduct(product)) {
      const themes =
        product.startingDestination?.country?.themeGeoPages?.themeGeoPages?.map(
          tgp => tgp.theme
        );

      return (
        <>
          <FdrAdventureTravelContent
            staticRequestButton={productPageContent.staticRequestButton}
            b2bInfoGroups={productPageContent.b2bInfoGroups}
            hotelPdfs={product.hotelPdfs}
            co2Footprint={product.co2Footprint}
          />
          <FdrPdpRecommendations
            id={id}
            countryName={product.startingDestination?.country?.name}
            themes={themes}
            blogArticles={product.relatedBlogArticles}
            products={product.startingDestination?.country?.products}
          />
        </>
      );
    }

    if (isFdrHotelProduct(product)) {
      const images = product.images.map(image => mapImageCriteriaField(image));

      return (
        <FdrHotelContent
          images={images}
          b2bInfoGroups={productPageContent.b2bInfoGroups}
        />
      );
    }

    if (isFdrRoundTripProduct(product)) {
      const images = product.images.map(image => mapImageCriteriaField(image));
      const mapImages = product.mapImages?.map(image =>
        mapImageCriteriaField(image)
      );

      return (
        <FdrRoundTripContent
          images={images}
          mapImages={mapImages}
          b2bInfoGroups={productPageContent.b2bInfoGroups}
        />
      );
    }

    return null;
  }

  return (
    <>
      {productPageContent.hero ? (
        <FdrHeroSection
          hero={productPageContent.hero}
          text={productPageContent.text}
          title={productPageContent.title}
          isHeroSiteToolsEnabled={!shareDisabled}
          bookmarked={productPageContent.bookmarked}
        >
          <FdrDisruptersContainer>
            <FdrDisrupters
              disrupters={productPageContent.disrupters}
              className="product-disrupters"
            />
          </FdrDisruptersContainer>
        </FdrHeroSection>
      ) : null}
      <FdrPdpDescription
        leadText={productPageContent.leadText}
        routes={productPageContent.routes}
        featureGroups={productPageContent.featureGroups}
        staticRequestButton={productPageContent.staticRequestButton}
      />
      {renderProductContent()}
      <FdrPageComponentsWithCustomBenefits
        components={productPageContent.pageComponents}
      />
    </>
  );
}
